import { useCallback, useEffect, useState } from 'react';
import { useMedplum } from '@medplum/react';
import { Patient } from '@medplum/fhirtypes';
import {
  BirthMeasurementsData,
  BirthMeasurementsFormState,
  UseBirthObservationsResult,
} from '../utils/medical-history.types';
import {
  fetchBirthObservations,
  saveBirthMeasurements,
} from '../utils/birth-observations.utils';

export const useBirthObservations = (
  patient: Patient | undefined
): UseBirthObservationsResult => {
  const medplum = useMedplum();

  const [data, setData] = useState<BirthMeasurementsData>({
    birthWeight: undefined,
    birthLength: undefined,
    birthHeadCircumference: undefined,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    if (!patient) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const observations = await fetchBirthObservations(medplum, patient);
      setData(observations);
    } catch (err) {
      console.error('Error fetching birth observations:', err);
      setError(
        err instanceof Error ? err : new Error('Failed to fetch birth data')
      );
    } finally {
      setLoading(false);
    }
  }, [medplum, patient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const saveBirthMeasurementsCallback = useCallback(
    async (formState: BirthMeasurementsFormState): Promise<void> => {
      if (!patient) {
        throw new Error('Patient is required');
      }

      // Fetch current data to ensure we have the latest observations
      const currentData = await fetchBirthObservations(medplum, patient);
      await saveBirthMeasurements(medplum, patient, formState, currentData);

      // Invalidate cache so subsequent reads get fresh data
      medplum.invalidateSearches('Observation');
    },
    [medplum, patient]
  );

  const deleteBirthMeasurementsCallback = useCallback(async (): Promise<void> => {
    if (!patient) {
      throw new Error('Patient is required');
    }

    try {
      // Delete all existing birth measurement observations
      const observationsToDelete: string[] = [];

      if (data.birthWeight?.id) {
        observationsToDelete.push(data.birthWeight.id);
      }
      if (data.birthLength?.id) {
        observationsToDelete.push(data.birthLength.id);
      }
      if (data.birthHeadCircumference?.id) {
        observationsToDelete.push(data.birthHeadCircumference.id);
      }

      // Delete all observations in parallel
      await Promise.all(
        observationsToDelete.map((id) =>
          medplum.deleteResource('Observation', id)
        )
      );

      // Clear the data from state
      setData({
        birthWeight: undefined,
        birthLength: undefined,
        birthHeadCircumference: undefined,
      });

      // Invalidate cache so subsequent reads get fresh data
      medplum.invalidateSearches('Observation');

      // Optionally refetch to ensure consistency
      await fetchData();
    } catch (err) {
      console.error('Error deleting birth measurements:', err);
      throw err;
    }
  }, [medplum, patient, data, fetchData]);

  return {
    data,
    loading,
    error,
    saveBirthMeasurements: saveBirthMeasurementsCallback,
    deleteBirthMeasurements: deleteBirthMeasurementsCallback,
    refetch: fetchData,
  };
};
